import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, SITUATIONS } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, ChevronRight, ChevronLeft, Check, Loader2 } from "lucide-react";

const PROPERTY_TYPES = ["Single Family","Condo","Townhome","Multi-family (2-4)","Mobile/Manufactured","Land","Other"];
const REPAIR_LEVELS = [
  { id: "none", label: "Move-in ready" },
  { id: "light", label: "Light (cosmetic)" },
  { id: "medium", label: "Medium ($10-30k)" },
  { id: "heavy", label: "Heavy ($30k+)" },
];
const TIMELINES = [
  { id: "asap", label: "ASAP (within 14 days)" },
  { id: "30_days", label: "Within 30 days" },
  { id: "60_days", label: "Within 60 days" },
  { id: "flexible", label: "Flexible" },
];
const OCCUPANCY = [
  { id: "owner", label: "Owner occupied" }, { id: "tenant", label: "Tenant occupied" },
  { id: "vacant", label: "Vacant" }, { id: "family", label: "Family member living there" },
];

export default function SubmitProperty() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    address_line1: "", city: "", state: "", zip_code: "",
    property_type: "Single Family", bedrooms: "", bathrooms: "", square_feet: "", year_built: "",
    estimated_value: "", mortgage_balance: "", monthly_payment: "", months_behind: "",
    situations: [], repair_level: "light", occupancy: "owner", timeline: "30_days",
    asking_price: "", notes: "", photo_urls: [], document_urls: [], intake_answers: {},
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSit = (id) =>
    setForm((f) => ({ ...f, situations: f.situations.includes(id) ? f.situations.filter((x) => x !== id) : [...f.situations, id] }));

  const onUpload = async (e, key) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      update(key, [...(form[key] || []), ...urls]);
      toast.success(`Uploaded ${files.length} file(s)`);
    } catch (err) {
      toast.error("Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { ...form };
      ["bedrooms","square_feet","year_built","months_behind"].forEach((k)=>{ payload[k] = payload[k]===""?null:parseInt(payload[k]); });
      ["bathrooms","estimated_value","mortgage_balance","monthly_payment","asking_price"].forEach((k)=>{ payload[k] = payload[k]===""?null:parseFloat(payload[k]); });
      const { data } = await api.post("/properties", payload);
      toast.success("Property submitted — AI is analyzing now");
      navigate(`/properties/${data.property_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const STEPS = [
    { id: "address", label: "Address" },
    { id: "details", label: "Details" },
    { id: "situation", label: "Situation" },
    { id: "photos", label: "Photos & Docs" },
    { id: "review", label: "Review" },
  ];

  const canNext = (() => {
    if (step === 0) return form.address_line1 && form.city && form.state && form.zip_code;
    if (step === 2) return form.situations.length > 0;
    return true;
  })();

  return (
    <DashboardLayout>
      <div className="max-w-3xl">
        <p className="eyebrow">Seller intake</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold mt-2">Tell us about your property</h1>
        <p className="text-[var(--adn-text-soft)] mt-2 text-sm">Free. No obligation. Sellers are matched with multiple options.</p>

        <ol className="mt-8 flex flex-wrap gap-2 text-xs">
          {STEPS.map((s, i) => (
            <li key={s.id} className={`px-3 py-1.5 border ${i === step ? "border-[var(--adn-gold)] text-[var(--adn-navy)] bg-white" : i < step ? "border-[var(--adn-emerald)] text-[var(--adn-emerald)] bg-white" : "border-[var(--adn-line)] text-[var(--adn-text-muted)] bg-white"}`}>
              {i + 1}. {s.label}
            </li>
          ))}
        </ol>

        <div className="mt-8 bg-white border border-[var(--adn-line)] p-6 md:p-8">
          {step === 0 && (
            <div className="space-y-4">
              <FormField label="Street address">
                <input data-testid="step1-address" className="input" value={form.address_line1} onChange={(e)=>update("address_line1", e.target.value)} placeholder="123 Main St" />
              </FormField>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="City"><input data-testid="step1-city" className="input" value={form.city} onChange={(e)=>update("city", e.target.value)} /></FormField>
                <FormField label="State"><input data-testid="step1-state" className="input" value={form.state} onChange={(e)=>update("state", e.target.value)} placeholder="TX" maxLength={2} /></FormField>
                <FormField label="ZIP"><input data-testid="step1-zip" className="input" value={form.zip_code} onChange={(e)=>update("zip_code", e.target.value)} /></FormField>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <FormField label="Property type">
                <select className="input" value={form.property_type} onChange={(e)=>update("property_type", e.target.value)}>
                  {PROPERTY_TYPES.map((p)=> <option key={p}>{p}</option>)}
                </select>
              </FormField>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <FormField label="Beds"><input className="input" type="number" value={form.bedrooms} onChange={(e)=>update("bedrooms", e.target.value)} /></FormField>
                <FormField label="Baths"><input className="input" type="number" step="0.5" value={form.bathrooms} onChange={(e)=>update("bathrooms", e.target.value)} /></FormField>
                <FormField label="Sq ft"><input className="input" type="number" value={form.square_feet} onChange={(e)=>update("square_feet", e.target.value)} /></FormField>
                <FormField label="Year built"><input className="input" type="number" value={form.year_built} onChange={(e)=>update("year_built", e.target.value)} /></FormField>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Estimated value ($)"><input className="input" type="number" value={form.estimated_value} onChange={(e)=>update("estimated_value", e.target.value)} /></FormField>
                <FormField label="Mortgage balance ($)"><input className="input" type="number" value={form.mortgage_balance} onChange={(e)=>update("mortgage_balance", e.target.value)} /></FormField>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FormField label="Monthly payment ($)"><input className="input" type="number" value={form.monthly_payment} onChange={(e)=>update("monthly_payment", e.target.value)} /></FormField>
                <FormField label="Months behind (if any)"><input className="input" type="number" value={form.months_behind} onChange={(e)=>update("months_behind", e.target.value)} /></FormField>
              </div>
              <FormField label="Repair level">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {REPAIR_LEVELS.map((r)=>(
                    <button key={r.id} type="button" onClick={()=>update("repair_level", r.id)} className={`text-left px-3 py-2 border text-sm ${form.repair_level === r.id ? "border-[var(--adn-gold)] bg-white" : "border-[var(--adn-line)] bg-white hover:border-[var(--adn-navy)]"}`}>{r.label}</button>
                  ))}
                </div>
              </FormField>
              <FormField label="Occupancy">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {OCCUPANCY.map((o)=>(
                    <button key={o.id} type="button" onClick={()=>update("occupancy", o.id)} className={`text-left px-3 py-2 border text-sm ${form.occupancy === o.id ? "border-[var(--adn-gold)] bg-white" : "border-[var(--adn-line)] bg-white"}`}>{o.label}</button>
                  ))}
                </div>
              </FormField>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <p className="font-display font-semibold text-lg">What's going on?</p>
                <p className="text-sm text-[var(--adn-text-soft)] mt-1">Pick all that apply. This drives your recommended paths.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-4">
                  {SITUATIONS.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`sit-${s.id}`}
                      onClick={()=>toggleSit(s.id)}
                      className={`text-left px-4 py-3 border text-sm flex items-center justify-between ${form.situations.includes(s.id) ? "border-[var(--adn-gold)] bg-white shadow-sm" : "border-[var(--adn-line)] bg-white hover:border-[var(--adn-navy)]"}`}
                    >
                      <span>{s.label}</span>
                      {form.situations.includes(s.id) && <Check size={16} className="text-[var(--adn-emerald)]" />}
                    </button>
                  ))}
                </div>
              </div>
              <FormField label="Timeline">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {TIMELINES.map((t)=>(
                    <button key={t.id} type="button" onClick={()=>update("timeline", t.id)} className={`text-left px-3 py-2 border text-sm ${form.timeline === t.id ? "border-[var(--adn-gold)] bg-white" : "border-[var(--adn-line)] bg-white"}`}>{t.label}</button>
                  ))}
                </div>
              </FormField>
              <FormField label="Desired asking price (optional)">
                <input className="input" type="number" value={form.asking_price} onChange={(e)=>update("asking_price", e.target.value)} />
              </FormField>
              <FormField label="Anything else we should know?">
                <textarea className="input min-h-[100px]" value={form.notes} onChange={(e)=>update("notes", e.target.value)} placeholder="Background, recent events, what you've already tried…" />
              </FormField>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <p className="font-display font-semibold">Photos</p>
                <p className="text-sm text-[var(--adn-text-soft)]">Exterior, interior rooms, problem areas. JPG/PNG, up to 25MB each.</p>
                <UploadButton id="photos" accept="image/*" onChange={(e)=>onUpload(e, "photo_urls")} uploading={uploading} count={form.photo_urls.length} />
              </div>
              <div>
                <p className="font-display font-semibold">Documents</p>
                <p className="text-sm text-[var(--adn-text-soft)]">Mortgage statement, notice, probate docs, tax bills. PDF preferred.</p>
                <UploadButton id="docs" accept=".pdf,image/*" onChange={(e)=>onUpload(e, "document_urls")} uploading={uploading} count={form.document_urls.length} />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 text-sm">
              <Row k="Address" v={`${form.address_line1}, ${form.city}, ${form.state} ${form.zip_code}`} />
              <Row k="Type" v={form.property_type} />
              <Row k="Beds/Baths/Sqft" v={`${form.bedrooms || "—"} / ${form.bathrooms || "—"} / ${form.square_feet || "—"}`} />
              <Row k="Value / Mortgage" v={`$${form.estimated_value || 0} / $${form.mortgage_balance || 0}`} />
              <Row k="Situations" v={form.situations.map(s => SITUATIONS.find(x=>x.id===s)?.label || s).join(", ") || "—"} />
              <Row k="Repair / Occupancy / Timeline" v={`${form.repair_level} / ${form.occupancy} / ${form.timeline}`} />
              <Row k="Uploads" v={`${form.photo_urls.length} photos · ${form.document_urls.length} documents`} />
              <p className="text-[var(--adn-text-muted)] text-xs">After submitting, AI will summarize your situation and you'll start receiving solution paths and offers.</p>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              data-testid="step-prev"
              disabled={step === 0}
              onClick={()=>setStep((s)=>Math.max(0,s-1))}
              className="btn-outline-navy px-5 py-2.5 rounded text-sm disabled:opacity-30 inline-flex items-center gap-2"
            >
              <ChevronLeft size={16} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                data-testid="step-next"
                disabled={!canNext}
                onClick={()=>setStep((s)=>Math.min(STEPS.length-1,s+1))}
                className="btn-gold px-6 py-2.5 rounded text-sm inline-flex items-center gap-2 disabled:opacity-50"
              >
                Continue <ChevronRight size={16} />
              </button>
            ) : (
              <button
                data-testid="submit-property"
                disabled={submitting}
                onClick={submit}
                className="btn-gold px-6 py-2.5 rounded text-sm inline-flex items-center gap-2"
              >
                {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit Property</>}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input { width: 100%; background: white; border: 1px solid #CBD5E1; padding: 10px 12px; font-size: 14px; color: var(--adn-text); transition: border 150ms ease, box-shadow 150ms ease; }
        .input:focus { outline: none; border-color: var(--adn-navy); box-shadow: 0 0 0 3px rgba(11,19,43,0.08); }
      `}</style>
    </DashboardLayout>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-[var(--adn-text-soft)] mb-2 block">{label}</span>
      {children}
    </label>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[var(--adn-line)] py-2.5">
      <div className="text-[var(--adn-text-soft)]">{k}</div>
      <div className="text-right max-w-xl">{v}</div>
    </div>
  );
}
function UploadButton({ id, accept, onChange, uploading, count }) {
  return (
    <label htmlFor={id} className={`mt-3 flex items-center justify-between gap-3 border border-dashed ${uploading ? "border-[var(--adn-gold)]" : "border-[var(--adn-line)]"} bg-white px-5 py-4 cursor-pointer hover:border-[var(--adn-navy)] transition-colors`}>
      <div className="flex items-center gap-3">
        {uploading ? <Loader2 className="animate-spin text-[var(--adn-gold)]" size={18} /> : <Upload size={18} className="text-[var(--adn-text-soft)]" />}
        <div>
          <div className="text-sm font-medium">{uploading ? "Uploading…" : "Click to upload files"}</div>
          <div className="text-xs text-[var(--adn-text-muted)]">{count} file(s) added</div>
        </div>
      </div>
      <input id={id} type="file" multiple accept={accept} onChange={onChange} className="hidden" data-testid={`upload-${id}`} />
    </label>
  );
}
